/**
 * @fileoverview Nome do contato montado pela triagem (aba "Cadastro do
 * contato"), em funções puras: os dados que podem ir no nome (botões), o
 * primeiro problema do formato, a prévia com dados de exemplo, quando o nome
 * muda e a inserção de um dado no cursor. As regras são as do servidor
 * (TriageServices/rules/contactNameRules.ts): mudou lá, mude aqui e nos testes.
 */

import { acceptsExtraction } from "./formState";

export const CONTACT_NAME_FORMAT_MAX = 80;
export const CONTACT_NAME_MAX_VARIABLES = 6;

// Os dados da OS no formato, na ordem dos botões, com o dado da tela de cada um.
export const WORK_ORDER_TOKENS = [
  { token: "os.modelo", field: "model" },
  { token: "os.marca", field: "brand" },
  { token: "os.categoria", field: "category" },
  { token: "os.tipo", field: "type" },
  { token: "os.garantia", field: "warranty" },
  { token: "os.cidade", field: "city" },
  { token: "os.uf", field: "state" },
];
const WORK_ORDER_FIELD = Object.fromEntries(WORK_ORDER_TOKENS.map((w) => [w.token, w.field]));
const SYSTEM = new Set(["os", "nome", "primeiro_nome"]);

/** O item "Nome e CPF do cliente": o primeiro de texto ligado ao comprador da OS (só com a OS). */
export const buyerItem = (items, workOrdersEnabled) =>
  (workOrdersEnabled && items.find((it) => it.inputType === "text" && it.workOrderField === "buyer")) || null;

const fieldsOf = (it) => (it.extraction.enabled && acceptsExtraction(it.inputType) ? it.extraction.fields : []);

/** O campo do item do nome que guarda o nome (o primeiro de texto). */
const buyerNameFieldKey = (buyer) => (buyer && fieldsOf(buyer).find((f) => f.type === "string")?.key) || null;

/**
 * Os botões: dados do contato, da OS (só com o início pelas OS) e os
 * confirmados na conversa. Ficam de fora CPF/CNPJ, campo sensível, áudio, vídeo,
 * foto ou documento sem campos e o campo do nome do item "Nome e CPF" (o "Nome
 * do cliente" já é ele, com o nome da OS ou o atual quando falta).
 */
export const tokenGroups = ({ items, workOrdersEnabled }) => {
  const buyer = buyerItem(items, workOrdersEnabled);
  const buyerName = buyerNameFieldKey(buyer);
  const itemTokens = [];
  items.forEach((it) => {
    if (!it.key || !acceptsExtraction(it.inputType)) return;
    const itemName = it.name.trim() || it.key;
    const fields = fieldsOf(it);
    if (fields.length) {
      fields.forEach((f) => {
        if (!f.key || f.sensitive || f.type === "cpf_cnpj") return;
        if (buyer && it.uid === buyer.uid && f.key === buyerName) return;
        itemTokens.push({ token: `${it.key}.${f.key}`, itemName, fieldLabel: f.label.trim() || f.key });
      });
    } else if (it.inputType === "text" && !SYSTEM.has(it.key)) {
      itemTokens.push({ token: it.key, itemName, fieldLabel: null });
    }
  });
  return {
    contact: [{ token: "nome" }, { token: "primeiro_nome" }],
    workOrder: workOrdersEnabled ? [{ token: "os" }, ...WORK_ORDER_TOKENS.map((w) => ({ token: w.token, field: w.field }))] : [],
    items: itemTokens,
  };
};

/** O formato em texto e dados ({...}); null se há chave sem par. */
export const splitFormat = (format) => {
  const parts = [];
  const variable = /\{([^{}]*)\}/g;
  let last = 0;
  let m;
  while ((m = variable.exec(format)) !== null) {
    const before = format.slice(last, m.index);
    if (/[{}]/.test(before)) return null;
    if (before) parts.push({ text: before });
    parts.push({ variable: m[1] });
    last = m.index + m[0].length;
  }
  const rest = format.slice(last);
  if (/[{}]/.test(rest)) return null;
  if (rest) parts.push({ text: rest });
  return parts;
};

/**
 * O que é cada dado: { kind: "workOrderNumber" | "workOrder" | "name" | "item", ... },
 * ou { problem: "workOrder" } (dado da OS sem o início pelas OS) ou { problem: "token" }.
 */
export const classifyToken = (variable, { items, workOrdersEnabled }) => {
  if (variable === "os" || variable.startsWith("os.")) {
    if (variable !== "os" && !Object.prototype.hasOwnProperty.call(WORK_ORDER_FIELD, variable)) return { problem: "token" };
    if (!workOrdersEnabled) return { problem: "workOrder" };
    return variable === "os" ? { kind: "workOrderNumber" } : { kind: "workOrder", field: WORK_ORDER_FIELD[variable] };
  }
  if (variable === "nome" || variable === "primeiro_nome") return { kind: "name", firstOnly: variable === "primeiro_nome" };
  const [key, fieldKey, ...extra] = variable.split(".");
  const it = extra.length ? null : items.find((x) => x.key === key);
  if (!it || !acceptsExtraction(it.inputType)) return { problem: "token" };
  const fields = fieldsOf(it);
  if (fieldKey === undefined) {
    return !fields.length && it.inputType === "text" ? { kind: "item", item: it, field: null } : { problem: "token" };
  }
  const field = fields.find((f) => f.key === fieldKey);
  if (!field || field.sensitive || field.type === "cpf_cnpj") return { problem: "token" };
  return { kind: "item", item: it, field };
};

/**
 * O primeiro problema do formato, na ordem do servidor, ou null:
 * { code: "empty" | "tooLong" | "lines" | "braces" | "noData" | "tooMany" }
 * ou { code: "token" | "workOrder", token: "{...}" } (como foi digitado).
 */
export const contactNameProblem = (format, ctx) => {
  const text = String(format || "").trim();
  if (!text) return { code: "empty" };
  if (text.length > CONTACT_NAME_FORMAT_MAX) return { code: "tooLong" };
  if (/[\r\n]/.test(text)) return { code: "lines" };
  const parts = splitFormat(text);
  if (!parts) return { code: "braces" };
  const variables = parts.filter((p) => p.variable !== undefined);
  for (const p of variables) {
    const c = classifyToken(p.variable, ctx);
    if (c.problem) return { code: c.problem, token: `{${p.variable}}` };
  }
  if (!variables.length) return { code: "noData" };
  if (variables.length > CONTACT_NAME_MAX_VARIABLES) return { code: "tooMany" };
  return null;
};

/**
 * A prévia: o texto, os dados do contato e da OS com o exemplo
 * (`examples[token]`), os dos itens como «Rótulo» e o dado inválido como foi
 * digitado. Partes: { type: "text" | "value" | "placeholder" | "invalid", text }.
 */
export const previewParts = (format, ctx, examples) => {
  const parts = splitFormat(String(format || ""));
  if (!parts) return [{ type: "invalid", text: String(format || "") }];
  return parts.map((p) => {
    if (p.variable === undefined) return { type: "text", text: p.text };
    const c = classifyToken(p.variable, ctx);
    if (c.problem) return { type: "invalid", text: `{${p.variable}}` };
    if (c.kind === "item") return { type: "placeholder", text: `«${c.field ? c.field.label.trim() || c.field.key : c.item.name.trim() || c.item.key}»` };
    return { type: "value", text: examples[p.variable] || "" };
  });
};

/**
 * Quando o nome muda (as regras do servidor): "start" (só dados da OS ou do
 * contato, sem nada a confirmar), "items" (quando a IA concluir os itens de
 * `items`, pelos nomes) ou "end" (o nome do cliente só fica certo na
 * conclusão: triagem pela OS sem item ligado a "Nome e CPF/CNPJ do cliente").
 * Dado inválido fica de fora; sem dado válido, null.
 */
export const whenItChanges = (format, ctx) => {
  const parts = splitFormat(String(format || ""));
  if (!parts) return null;
  const buyer = buyerItem(ctx.items, ctx.workOrdersEnabled);
  const waits = [];
  let valid = 0;
  let nameAtEnd = false;
  const wait = (it) => {
    const name = it.name.trim() || it.key;
    if (!waits.includes(name)) waits.push(name);
  };
  parts.forEach((p) => {
    if (p.variable === undefined) return;
    const c = classifyToken(p.variable, ctx);
    if (c.problem) return;
    valid += 1;
    if (c.kind === "item") wait(c.item);
    if (c.kind === "name") {
      if (buyer) wait(buyer);
      else if (ctx.workOrdersEnabled) nameAtEnd = true;
    }
  });
  if (!valid) return null;
  if (nameAtEnd) return { moment: "end", items: waits };
  return waits.length ? { moment: "items", items: waits } : { moment: "start", items: [] };
};

const glued = (ch) => !!ch && (/[\p{L}\p{N}]/u.test(ch) || ch === "{" || ch === "}");

/**
 * O formato com o dado `{token}` no lugar da seleção (ou no cursor), com um
 * espaço só quando colaria numa palavra ou noutro dado ("{os}-" + nome dá
 * "{os}-{nome}"). Devolve o texto e a posição do cursor depois do dado.
 */
export const insertToken = (format, selection, token) => {
  const text = String(format || "");
  const start = Math.max(0, Math.min(selection?.start ?? text.length, text.length));
  const end = Math.max(start, Math.min(selection?.end ?? start, text.length));
  const before = text.slice(0, start);
  const after = text.slice(end);
  const inserted = `{${token}}`;
  const spaceBefore = glued(before.slice(-1)) ? " " : "";
  const spaceAfter = glued(after.charAt(0)) ? " " : "";
  return {
    value: `${before}${spaceBefore}${inserted}${spaceAfter}${after}`,
    caret: before.length + spaceBefore.length + inserted.length,
  };
};
